import { useState, useCallback, useRef, useLayoutEffect } from 'react';
import { NODE_WIDTH, NODE_HEIGHT } from './utils/canvasUtils';
import { hasAnyConditions } from '../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * Hook for managing flowchart panning state and handlers
 * 
 * @param {Object} effectivePositionsRef - Optional ref to positions that should be used for calculations
 *                                         (e.g., FOKUS mode positions). When set, these override nodePositions.
 */
export function useFlowchartPan(containerRef, nodePositions, customPositions, tasks = [], nodeDimensions = {}, effectivePositionsRef = null) {
  const [pan, setPanState] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  
  // Track when camera is animating (blocks interactions during pan animation)
  const [isAnimatingPan, setIsAnimatingPan] = useState(false);
  
  // Keep a ref to the latest pan value to avoid stale closures
  // Update both state AND ref synchronously
  const panRef = useRef(pan);
  
  // Keep a ref to the latest zoom value (updated by useViewportConstraints)
  const zoomRef = useRef(1);
  
  // Animation ref for cleanup
  const animationRef = useRef(null);
  
  // Keep refs to latest values to avoid stale closures in callbacks
  const tasksRef = useRef(tasks);
  const nodePositionsRef = useRef(nodePositions);
  const customPositionsRef = useRef(customPositions);
  const nodeDimensionsRef = useRef(nodeDimensions);
  
  // Sync refs with props
  useLayoutEffect(() => {
    tasksRef.current = tasks;
    nodePositionsRef.current = nodePositions;
    customPositionsRef.current = customPositions;
    nodeDimensionsRef.current = nodeDimensions;
  }, [tasks, nodePositions, customPositions, nodeDimensions]);
  
  // Sync ref with state (catches external updates)
  useLayoutEffect(() => {
    panRef.current = pan;
  }, [pan]);
  
  // Helper to update DOM transform directly
  // This is called by setPan to keep DOM in sync with React state
  const updateDOMTransform = useCallback((newPan, zoom) => {
    const canvas = containerRef.current?.querySelector('.flowchart__canvas');
    if (canvas) {
      canvas.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${zoom})`;
      canvas.style.setProperty('--inverse-zoom', 1 / zoom);
      canvas.dispatchEvent(new CustomEvent('panupdate', { 
        detail: { x: newPan.x, y: newPan.y, zoom } 
      }));
    }
  }, [containerRef]);
  
  // setPan updates React state and optionally the DOM
  // skipDOMUpdate: set to true if caller already updated DOM (e.g., during animations)
  const setPan = useCallback((newPan, skipDOMUpdate = false) => {
    let resolvedPan;
    if (typeof newPan === 'function') {
      resolvedPan = newPan(panRef.current);
    } else {
      resolvedPan = newPan;
    }
    
    // Update ref and state
    panRef.current = resolvedPan;
    setPanState(resolvedPan);
    
    // Update DOM directly (FlowchartCanvas no longer has useLayoutEffect for transform)
    if (!skipDOMUpdate) {
      updateDOMTransform(resolvedPan, zoomRef.current);
    }
  }, [updateDOMTransform]);

  // Start panning - uses ref to always get latest pan value
  const startPanning = useCallback((e) => {
    setIsPanning(true);
    setPanStart({
      x: e.clientX - panRef.current.x,
      y: e.clientY - panRef.current.y,
    });
  }, []);

  // Update pan position during mouse move
  const updatePan = useCallback((e) => {
    if (!isPanning) return false;
    setPan({
      x: e.clientX - panStart.x,
      y: e.clientY - panStart.y,
    });
    return true;
  }, [isPanning, panStart]);

  // Stop panning
  const stopPanning = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Helper to get position for a task - checks effectivePositions, customPositions, nodePositions, and task.x/y
  // Uses refs to always get latest values (avoids stale closures)
  const getTaskPosition = useCallback((task) => {
    // When effectivePositionsRef is set (FOKUS mode), ONLY return positions for tasks in that object
    // This ensures Start button and other calculations only consider visible tasks
    const effectivePositions = effectivePositionsRef?.current;
    if (effectivePositions) {
      // In FOKUS mode - only return position if task is in effectivePositions
      return effectivePositions[task.id] || null;
    }
    
    // Normal mode - check all position sources
    const currentCustomPositions = customPositionsRef.current;
    const currentNodePositions = nodePositionsRef.current;
    if (currentCustomPositions[task.id]) return currentCustomPositions[task.id];
    if (currentNodePositions[task.id]) return currentNodePositions[task.id];
    if (task.x !== undefined && task.y !== undefined) return { x: task.x, y: task.y };
    return null;
  }, [effectivePositionsRef]); // Depends on effectivePositionsRef

  // Reset view to center content
  // Uses refs to always get latest values (avoids stale closures)
  const handleResetView = useCallback((setZoom) => {
    if (!containerRef.current) {
      setPan({ x: 0, y: 0 });
      setZoom(1);
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const currentTasks = tasksRef.current;
    
    // Get all task positions from all sources
    const allPositions = currentTasks.map(task => getTaskPosition(task)).filter(Boolean);
    
    if (allPositions.length === 0) {
      setPan({ x: 0, y: 0 });
      setZoom(1);
      return;
    }
    
    const minX = Math.min(...allPositions.map(p => p.x));
    const maxX = Math.max(...allPositions.map(p => p.x + NODE_WIDTH));
    const minY = Math.min(...allPositions.map(p => p.y));
    
    const contentWidth = maxX - minX;
    const centerX = (containerWidth - contentWidth) / 2 - minX;
    const topPadding = 40;
    const panY = topPadding - minY;
    
    setPan({ x: centerX, y: panY });
    setZoom(1);
  }, [containerRef, getTaskPosition, setPan]); // Uses refs for tasks

  // Calculate the ideal "start" position (smart centering)
  // Returns { pan: {x, y}, zoom: number } or null if can't calculate
  // Uses refs to always get latest values (avoids stale closures)
  const calculateStartPosition = useCallback((containerWidth, containerHeight) => {
    const currentTasks = tasksRef.current;
    const currentNodeDimensions = nodeDimensionsRef.current;
    
    // Get ALL task positions to calculate bounding box
    const allPositions = currentTasks.map(task => getTaskPosition(task)).filter(Boolean);

    if (allPositions.length === 0) {
      return { pan: { x: 0, y: 0 }, zoom: 1 };
    }

    const targetZoom = 1;
    
    // Calculate bounding box of ALL nodes using actual dimensions from nodeDimensions
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    currentTasks.forEach(task => {
      const pos = getTaskPosition(task);
      if (!pos) return;
      const dims = currentNodeDimensions[task.id] || { width: NODE_WIDTH, height: NODE_HEIGHT };
      minX = Math.min(minX, pos.x);
      minY = Math.min(minY, pos.y);
      maxX = Math.max(maxX, pos.x + dims.width);
      maxY = Math.max(maxY, pos.y + dims.height);
    });
    
    const contentWidth = (maxX - minX) * targetZoom;
    const contentHeight = (maxY - minY) * targetZoom;
    
    // Padding for when we align to edges (top-left fallback)
    const edgePaddingX = Math.min(containerWidth * 0.1, 60);
    const edgePaddingY = Math.min(containerHeight * 0.1, 60);
    
    // Check if content fits in viewport
    const fitsHorizontally = contentWidth + (edgePaddingX * 2) <= containerWidth;
    const fitsVertically = contentHeight + (edgePaddingY * 2) <= containerHeight;
    
    let targetPanX, targetPanY;
    
    if (fitsHorizontally) {
      const contentCenterX = (minX + maxX) / 2;
      targetPanX = (containerWidth / 2) - (contentCenterX * targetZoom);
    } else {
      // Find root tasks (no dependencies from either system)
      const rootTasks = currentTasks.filter(task => {
        const hasConditions = hasAnyConditions(task.unlockConditions);
        const hasDependsOn = task.dependsOn && task.dependsOn.length > 0;
        return !hasConditions && !hasDependsOn;
      });
      const rootPositions = rootTasks.map(task => getTaskPosition(task)).filter(Boolean);
      const targetPositions = rootPositions.length > 0 ? rootPositions : allPositions;
      
      const topRowTolerance = 20;
      const topRowPositions = targetPositions.filter(p => p.y <= minY + topRowTolerance);
      const topLeftPos = topRowPositions.reduce((leftmost, pos) => 
        pos.x < leftmost.x ? pos : leftmost
      , topRowPositions[0]);
      
      const leftPadding = Math.min(containerWidth * 0.3, 200);
      targetPanX = leftPadding - topLeftPos.x * targetZoom;
    }
    
    if (fitsVertically) {
      const contentCenterY = (minY + maxY) / 2;
      targetPanY = (containerHeight / 2) - (contentCenterY * targetZoom);
    } else {
      const topPadding = Math.min(containerHeight * 0.2, 100);
      targetPanY = topPadding - minY * targetZoom;
    }

    return { pan: { x: targetPanX, y: targetPanY }, zoom: targetZoom };
  }, [getTaskPosition]); // Only depends on getTaskPosition, uses refs for other values

  // Navigate to start nodes with smooth animation
  // Uses calculateStartPosition for the target, then animates to it
  const handleGoToStart = useCallback((setZoom, currentZoom = 1) => {
    if (!containerRef.current) {
      setPan({ x: 0, y: 0 });
      setZoom(1, true);
      return;
    }

    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const result = calculateStartPosition(containerRect.width, containerRect.height);
    
    if (!result) {
      setPan({ x: 0, y: 0 });
      setZoom(1, true);
      return;
    }

    const { pan: targetPan, zoom: targetZoom } = result;
    const targetPanX = targetPan.x;
    const targetPanY = targetPan.y;

    // Get current values for animation
    const startPan = { ...panRef.current };
    const startZoom = currentZoom;
    const startTime = performance.now();
    const duration = 300; // 300ms for smooth animation
    
    // Signal animation start (blocks interactions)
    setIsAnimatingPan(true);
    // IMMEDIATELY show blocker to prevent interaction during animation
    const blocker = document.querySelector('.flowchart__animation-blocker');
    if (blocker) blocker.style.display = 'block';
    
    // Ease out cubic for smooth deceleration
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      
      const newZoom = startZoom + (targetZoom - startZoom) * easedProgress;
      const newPanX = startPan.x + (targetPanX - startPan.x) * easedProgress;
      const newPanY = startPan.y + (targetPanY - startPan.y) * easedProgress;
      
      // Update refs
      panRef.current = { x: newPanX, y: newPanY };
      zoomRef.current = newZoom;
      
      // Update DOM directly for smoother animation
      const canvas = containerRef.current?.querySelector('.flowchart__canvas');
      if (canvas) {
        canvas.style.transform = `translate(${newPanX}px, ${newPanY}px) scale(${newZoom})`;
        // Update CSS variable for zoom-independent elements
        canvas.style.setProperty('--inverse-zoom', 1 / newZoom);
        // Dispatch event for minimap to sync viewport
        canvas.dispatchEvent(new CustomEvent('panupdate', { detail: { x: newPanX, y: newPanY, zoom: newZoom } }));
      }
      
      // Update React state (skip DOM update since we already did it)
      setZoom(newZoom, true);
      setPan({ x: newPanX, y: newPanY }, true);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        animationRef.current = null;
        // Signal animation end (unblocks interactions)
        setIsAnimatingPan(false);
        // Hide blocker when animation ends
        const blocker = document.querySelector('.flowchart__animation-blocker');
        if (blocker) blocker.style.display = '';
      }
    };
    
    // Start animation
    animationRef.current = requestAnimationFrame(animate);
  }, [containerRef, calculateStartPosition, setPan]);

  // Navigate to start nodes INSTANTLY (no animation - for view switching)
  const handleGoToStartInstant = useCallback((setZoom) => {
    if (!containerRef.current) {
      setPan({ x: 0, y: 0 });
      setZoom(1, true);
      return;
    }

    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const result = calculateStartPosition(containerRect.width, containerRect.height);
    
    if (!result) {
      setPan({ x: 0, y: 0 });
      setZoom(1, true);
      return;
    }

    const { pan: targetPan, zoom: targetZoom } = result;
    
    // Set position immediately without animation
    panRef.current = targetPan;
    zoomRef.current = targetZoom;
    setPan(targetPan);
    setZoom(targetZoom, true);
  }, [containerRef, calculateStartPosition, setPan]);

  return {
    pan,
    setPan,
    panRef,
    zoomRef, // Expose so useViewportConstraints can update it
    isPanning,
    isAnimatingPan, // True during camera pan animations (blocks interactions)
    panStart,
    startPanning,
    updatePan,
    stopPanning,
    handleResetView,
    handleGoToStart,
    handleGoToStartInstant, // For immediate positioning without animation (view switching)
    calculateStartPosition,
  };
}

